'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ChecklistItem {
  category: string;
  items: string[];
}

const ConsultationChecklist = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const checklistData: ChecklistItem[] = [
    {
      category: 'Before the Consultation',
      items: [
        'Gather inspiration images from Pinterest, Instagram, or magazines',
        'List your must-have features and non-negotiables',
        'Measure your space dimensions (length, width, height)',
        'Note existing furniture you want to keep or repurpose',
        'Prepare a rough budget range for the project',
        'List any structural constraints or building regulations'
      ]
    },
    {
      category: 'During the Consultation',
      items: [
        'Share your lifestyle needs and daily routines',
        'Discuss family members and their specific requirements',
        'Mention any cultural or religious design preferences',
        'Talk about storage needs and organization challenges',
        'Share color preferences and materials you like/dislike',
        'Ask about timeline, process, and payment structure'
      ]
    },
    {
      category: 'Questions to Ask',
      items: [
        'What is your design approach for my space?',
        'Can you show similar projects you\'ve completed?',
        'How do you handle budget constraints?',
        'What is the typical project timeline?',
        'How often will we have design reviews?',
        'What happens if I want to make changes mid-project?',
        'Do you provide 3D visualizations?',
        'What warranty do you offer on your work?'
      ]
    },
    {
      category: 'Documents to Bring',
      items: [
        'Floor plan or layout drawings (if available)',
        'Photos of current space from multiple angles',
        'List of existing furniture with measurements',
        'Building society guidelines (if applicable)',
        'Budget breakdown or financial planning notes'
      ]
    }
  ];

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-2xl shadow-soft p-8 lg:p-12">
        <div className="h-8 bg-muted/20 rounded w-2/3 mb-6 animate-pulse" />
        <div className="space-y-6">
          <div className="h-32 bg-muted/20 rounded animate-pulse" />
          <div className="h-32 bg-muted/20 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl shadow-soft p-6 md:p-8 lg:p-12">
      <div className="flex flex-col md:flex-row items-start justify-between mb-6 md:mb-8 gap-4">
        <div>
          <h2 className="font-headline text-2xl md:text-3xl font-bold text-foreground mb-2">
            Consultation Preparation Checklist
          </h2>
          <p className="font-body text-muted-foreground">
            Make the most of your consultation by coming prepared
          </p>
        </div>
        <div className="w-12 h-12 md:w-16 md:h-16 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
          <Icon name="ClipboardDocumentCheckIcon" size={24} className="text-primary md:hidden" />
          <Icon name="ClipboardDocumentCheckIcon" size={32} className="text-primary hidden md:block" />
        </div>
      </div>

      <div className="space-y-6 md:space-y-8">
        {checklistData.map((section, sectionIndex) => (
          <div key={sectionIndex} className="bg-background rounded-xl p-6">
            <h3 className="font-body font-semibold text-foreground mb-4 flex items-center">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mr-3">
                <span className="font-body text-sm font-bold text-primary">
                  {sectionIndex + 1}
                </span>
              </div>
              {section.category}
            </h3>
            
            <ul className="space-y-3">
              {section.items.map((item, itemIndex) => (
                <li key={itemIndex} className="flex items-start group">
                  <div className="w-5 h-5 border-2 border-border rounded flex items-center justify-center mt-0.5 mr-3 flex-shrink-0 group-hover:border-primary transition-colors">
                    <Icon name="CheckIcon" size={12} className="text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <span className="font-body text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-gradient-to-br from-accent/10 to-primary/10 rounded-xl p-6">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon name="LightBulbIcon" size={24} className="text-primary-foreground" />
          </div>
          <div>
            <h4 className="font-body font-semibold text-foreground mb-2">Pro Tip</h4>
            <p className="font-body text-sm text-muted-foreground">
              Create a digital folder with all your inspiration images, measurements, and notes. Share it with us before the consultation so we can come prepared with relevant ideas and suggestions tailored to your vision.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-8 text-center">
        <button
          onClick={() => {
            const formSection = document.getElementById('contact-form');
            if (formSection) {
              formSection.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          className="inline-flex items-center space-x-2 px-8 py-4 bg-primary text-primary-foreground font-body font-semibold rounded-lg hover:bg-primary/90 transition-all duration-200 shadow-soft hover:shadow-strong active:scale-95 min-h-[44px]"
        >
          <Icon name="CalendarIcon" size={20} />
          <span>Schedule Your Consultation</span>
        </button>
      </div>
    </div>
  );
};

export default ConsultationChecklist;